import { useEffect, useState } from 'react';
import { Group, Loader, Progress, Stack, Text } from '@mantine/core';
import { Check, Circle } from 'lucide-react';
import { Card, StatusDot } from '../ui';
import {
  deriveProvisioningStages,
  provisioningEta,
  type ProvisioningStage,
} from './provisioning';
import type { RangeHealthVm } from '../../api/types';

/**
 * F2 — the provisioning experience for the ~10-minute deploy wait. Replaces the
 * opaque 0%-attack-progress bar with a live stage stepper (Network → Instances
 * → SSM online → Detection → Ready), an elapsed/ETA readout, and a per-VM
 * "coming online" list — all fed by the existing per-VM health poll.
 */
export interface ProvisioningPanelProps {
  status: string;
  vms: ReadonlyArray<RangeHealthVm>;
  vmCount: number;
  startedAt: string | null;
}

function clock(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  return `${m}:${String(total % 60).padStart(2, '0')}`;
}

export function ProvisioningPanel({ status, vms, vmCount, startedAt }: ProvisioningPanelProps) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  const stages = deriveProvisioningStages({ status, vms, vmCount });
  const pct = Math.round((stages.filter((s) => s.state === 'done').length / stages.length) * 100);
  const { elapsedMs, remainingMs } = provisioningEta(startedAt, now);
  const total = vmCount || vms.length;
  const online = vms.filter((v) => v.ssmStatus === 'Online' || v.healthy).length;

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            Provisioning
          </Text>
          {elapsedMs !== null && (
            <Text size="xs" c="dimmed" className="font-mono">
              {clock(elapsedMs)} elapsed
              {remainingMs ? ` · ~${clock(remainingMs)} left (est.)` : ''}
            </Text>
          )}
        </Group>

        <Progress value={pct} radius="xl" size="md" color="neon" />

        <Stack gap={0}>
          {stages.map((stage, i) => (
            <StageRow key={stage.id} stage={stage} isLast={i === stages.length - 1} />
          ))}
        </Stack>

        {vms.length > 0 && (
          <Stack gap={6}>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Hosts coming online · {online} / {total}
            </Text>
            {vms.map((vm) => {
              const up = vm.ssmStatus === 'Online' || vm.healthy;
              return (
                <Group
                  key={vm.instanceId}
                  justify="space-between"
                  wrap="nowrap"
                  style={{
                    padding: '6px 10px',
                    border: '1px solid var(--c-bd)',
                    borderRadius: 8,
                  }}
                >
                  <Group gap={8} wrap="nowrap">
                    <StatusDot state={up ? 'running' : 'idle'} size="xs" />
                    <Text size="sm" className="font-mono">
                      {vm.vmName || vm.hostname}
                    </Text>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {up ? 'online' : vm.state === 'running' ? 'booting' : vm.state || 'pending'}
                  </Text>
                </Group>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function StageRow({ stage, isLast }: { stage: ProvisioningStage; isLast: boolean }) {
  const tint =
    stage.state === 'done'
      ? 'rgb(var(--c-neon))'
      : stage.state === 'active'
        ? 'rgb(var(--c-cyan))'
        : 'rgb(var(--c-txt-muted))';
  return (
    <Group gap="md" wrap="nowrap" align="stretch">
      <Stack gap={0} align="center" style={{ minWidth: 26 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            border: `1.5px solid ${tint}`,
            color: tint,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {stage.state === 'active' ? (
            <Loader size={13} color="cyan" />
          ) : stage.state === 'done' ? (
            <Check size={13} />
          ) : (
            <Circle size={13} />
          )}
        </div>
        {!isLast && <div style={{ flex: 1, width: 1.5, background: 'var(--c-bd)', marginTop: 2 }} />}
      </Stack>
      <Stack gap={2} pb={isLast ? 0 : 'sm'} style={{ flex: 1 }}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" fw={600} c={stage.state === 'pending' ? 'dimmed' : 'rgb(var(--c-txt))'}>
            {stage.label}
          </Text>
          {stage.detail && stage.state !== 'pending' && (
            <Text size="xs" c="dimmed">
              {stage.detail}
            </Text>
          )}
        </Group>
      </Stack>
    </Group>
  );
}
